import { FormControl } from '@angular/forms';
import { TFilterSave } from 'scw-digital-boards-widgets';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IScreenLockPayload } from '../../../shared/components/screen-lock-configuration/screen-lock-configuration.interface';
import { EDashboardLayout } from '../../dashboard-content/dashboard-content.interface';
import { TRefreshIntervalOption } from '../../dashboard-content/widget-add-edit-dialog/widget-add-edit-dialog.interface';

export type TRefreshIntervalOptionsWithoutDashboardDefault = Exclude<TRefreshIntervalOption, 0>;

export type TFormControlDashboard = {
  admins: FormControl<IDropdownOption[] | null>;
  autoLockDuration: FormControl<IDropdownOption[] | null>;
  autoLockPin: FormControl<string | null>;
  isHidden: FormControl<boolean>;
  isPublished: FormControl<boolean>;
  isWidgetFilterButtonHidden: FormControl<boolean>;
  layoutMode: FormControl<IDropdownOption[] | null>;
  name: FormControl<string | null>;
  parent: FormControl<IDropdownOption[] | null>;
  refreshInterval: FormControl<IDropdownOption<TRefreshIntervalOptionsWithoutDashboardDefault>[] | null>;
  teams: FormControl<IDropdownOption[] | null>;
  users: FormControl<IDropdownOption[] | null>;
  workspace: FormControl<IDropdownOption[]>;
};

export type TFormDashboardComponent = {
  admins?: IDropdownOption[];
  autoLockDuration?: IDropdownOption[] | null;
  autoLockPin?: string | null;
  isHidden: boolean;
  isPublished: boolean;
  isWidgetFilterButtonHidden: boolean;
  name: string;
  refreshInterval: IDropdownOption<TRefreshIntervalOptionsWithoutDashboardDefault>[];
  teams?: IDropdownOption[];
  users?: IDropdownOption[];
  workspace: IDropdownOption[];
  parent: IDropdownOption[];
  layoutMode: IDropdownOption[];
};

export interface IDashboard {
  dashboardTeamAssignments: IDashboardTeamAssignment[];
  dashboardUserAssignments: IDashboardUserAssignment[];
  parent: IDashboard;
  defaultFilterCard: TFilterSave[] | null;
  id: number;
  isCurrentUserAdmin: boolean;
  isHidden: boolean;
  isPinned: boolean;
  isPublished: boolean;
  isWidgetFilterButtonHidden: boolean;
  layoutModeId: EDashboardLayout;
  name: string;
  refreshInterval: Exclude<TRefreshIntervalOptionsWithoutDashboardDefault, 0> | null;
  autoLockDurationSeconds: number | null;
  isAutoLockConfigured: boolean;
  workspace: IWorkspace;
  workspaceId: number;
  isInUseAsASubDashboard: boolean;
  parentId?: number | null;
  children: TDashboardChild[];
}

/** Only the fields the `children||…` join selects. */
export type TDashboardChild = Pick<IDashboard, 'id' | 'name' | 'isHidden'>;

export type TAddDashboard = Pick<
  IDashboard,
  | 'refreshInterval'
  | 'name'
  | 'workspaceId'
  | 'isHidden'
  | 'isPublished'
  | 'isWidgetFilterButtonHidden'
  | 'layoutModeId'
> &
  IScreenLockPayload & {
    adminIds?: number[];
    teamIds?: number[];
    userIds?: number[];
    parentId?: number | null;
  };

export interface IEditDashboard {
  dto: Partial<TAddDashboard & Pick<IDashboard, 'defaultFilterCard'>>;
  id: number;
}

export interface IDashboardBulkEditRequest {
  id: number;
  refreshInterval?: number | null;
}

export interface IBoardUserAssignmentUserJoin {
  id: number;
  isAdmin: boolean;
  name: string;
}

export interface IBoardUserAssignment {
  boardId: number;
  isAdmin: boolean;
  user: IBoardUserAssignmentUserJoin;
  userId: number;
}

export interface IBoardTeamAssignmentBase {
  boardId: number;
  team: { id: number; name: string };
  teamId: number;
}

export interface IWorkspaceModules {
  oeeTracker: boolean;
  assetTracker: boolean;
  digitalForms: boolean;
  laborTracker: boolean;
  actionTracker: boolean;
  stationTracker: boolean;
  scheduler: boolean;
  utilityWidgets: boolean;
}

export interface IWorkspace {
  id: number;
  name: string;
  dashboardLimit: number;
  configuration: IWorkspaceConfiguration | null;
  modulePermissions: IWorkspaceModules;
}

export interface IDashboardUserAssignment {
  id: number;
  userId: number;
  isAdmin: boolean;
}

export interface IDashboardTeamAssignment {
  id: number;
  teamId: number;
}

export interface IWorkspaceConfiguration {
  colors: {
    activity: {
      plannedDownTime: {
        button: string;
        buttonText: string;
        card: string;
        cardText: string;
      };
    };
  };
}

export interface IDashboardCard {
  id: number;
  name: string;
  refreshInterval: number | null;
  workspace: string;
  isCurrentUserAdmin: boolean;
  isPinned: boolean;
  isPrivate: boolean;
  isAutoLockConfigured: boolean;
  parentId: number | null;
  hasVisibleChildren: boolean;
  isHidden: boolean;
}

export interface ICloneDashboard {
  name: string;
  id: number;
}

export enum EDashboardViewMode {
  onlyVisible = 'onlyVisible',
  onlySubDashboards = 'onlySubDashboards',
  all = 'all',
  slideshows = 'slideshows',
}
